import { i18n } from "@lingui/core";
import { I18nProvider } from "@lingui/react";
import * as Sentry from "@sentry/react";
import { invariantResponse } from "@winamp/invariant";
import { BytescaleContextProvider } from "@winamp/media-upload/BytescaleContext/index.js";
import { getToast } from "@winamp/toast/index.js";
import { ClientOnly } from "@winamp/ui/components/ClientOnly/ClientOnly.js";
import { ToastContextProvider } from "@winamp/ui/components/Toast/ToastContextProvider.js";
import { parseWithValibot } from "conform-to-valibot";
import {
  type ActionFunctionArgs,
  Links,
  type LinksFunction,
  type LoaderFunctionArgs,
  Meta,
  Outlet,
  Scripts,
  ScrollRestoration,
  useRouteLoaderData,
} from "react-router";
import { data as json, redirect } from "react-router";
import { $path } from "safe-routes";
import appStylesheetUrl from "./app.css?url";
import { GeneralErrorBoundary } from "./components/ErrorBoundary";
import { GoogleScripts } from "./components/GoogleScripts";
import { Header } from "./components/Header";
import { MetaScripts } from "./components/MetaScripts";
import { ProgressBar } from "./components/ProgressBar";
import { environment } from "./config/env.server";
import { optionalAuth } from "./modules/auth/auth.server";
import { useOptionalAuth } from "./modules/auth/hooks";
import { getContextFromSession } from "./modules/auth/session.server";
import { getOAuth2Strategy } from "./modules/auth/strategy.server";
import { ClientHintCheck, getHints, useTheme } from "./modules/client-hints";
import { useEnv } from "./modules/env/useEnv";
import { loadCatalog, useLocale } from "./modules/lingui/lingui";
import { linguiServer, localeCookie } from "./modules/lingui/lingui.server";
import { ListingDisplayStateFormSchema } from "./modules/listing/form-schema";
import {
  getListingDisplayStates,
  setListingDisplayState,
} from "./modules/listing/listing-display-states.server";
import { getPermissions, hasPermissions } from "./modules/permissions/helpers";
import { getDomainUrl } from "./modules/request/misc";
import { useRequestToast } from "./modules/request/request-toast";
import { RouteDialogContextProvider } from "./modules/route-dialogs/RouteDialogsContext";
import { TableColumnsStateUpdateSchema } from "./modules/tables/form-schema";
import {
  getTablesStates,
  setTableState,
} from "./modules/tables/tables-states.server";
import { ThemeFormSchema } from "./modules/theme/form-schema";
import { getTheme, setTheme } from "./modules/theme/theme.server";

export type RouteHandle = {
  withoutSidebar?: boolean;
};

export const config = {
  maxDuration: 300,
};

export const links: LinksFunction = () => {
  return [
    { rel: "preconnect", href: "https://fonts.googleapis.com" },
    {
      rel: "preconnect",
      href: "https://fonts.gstatic.com",
      crossOrigin: "anonymous",
    },
    { rel: "stylesheet", href: appStylesheetUrl },
    {
      rel: "apple-touch-icon",
      sizes: "180x180",
      href: "/static/apple-touch-icon.png",
    },
    {
      rel: "icon",
      type: "image/png",
      sizes: "32x32",
      href: "/static/favicon-32x32.png",
    },
    {
      rel: "icon",
      type: "image/png",
      sizes: "16x16",
      href: "/static/favicon-16x16.png",
    },
    { rel: "manifest", href: "/static/site.webmanifest" },
  ];
};

export async function action({ request }: ActionFunctionArgs) {
  let formData: FormData;
  try {
    formData = await request.clone().formData();
  } catch (error) {
    Sentry.captureException(error, {
      extra: { request, requestText: await request.text() },
    });
    throw new Response("Invalid form data", { status: 400 });
  }

  switch (formData.get("intent")) {
    case "tables:state": {
      const submission = parseWithValibot(formData, {
        schema: TableColumnsStateUpdateSchema,
      });

      invariantResponse(
        submission.status === "success",
        "Invalid table state received",
      );

      const responseInit = {
        headers: {
          "set-cookie": setTableState(request, submission.value),
        },
      };

      return json(
        {
          result: submission.reply(),
        },
        responseInit,
      );
    }
    case "listing:display": {
      const submission = parseWithValibot(formData, {
        schema: ListingDisplayStateFormSchema,
      });

      invariantResponse(
        submission.status === "success",
        "Invalid listing display state received",
      );

      const responseInit = {
        headers: {
          "set-cookie": setListingDisplayState(request, submission.value),
        },
      };

      return json(
        {
          result: submission.reply(),
        },
        responseInit,
      );
    }
    case "theme": {
      const submission = parseWithValibot(formData, {
        schema: ThemeFormSchema,
      });

      invariantResponse(
        submission.status === "success",
        "Invalid theme received",
      );

      const { theme } = submission.value;

      const responseInit = {
        headers: { "set-cookie": setTheme(theme) },
      };

      return json(
        {
          result: submission.reply(),
        },
        responseInit,
      );
    }
  }

  Sentry.captureException("Unsupported intent", {
    extra: { request, formData: Object.fromEntries(formData.entries()) },
  });

  throw new Response(
    `The given intent "${formData.get("intent")}" is not supported`,
    { status: 400 },
  );
}

export async function loader({ request }: LoaderFunctionArgs) {
  const url = new URL(request.url);
  const auth = await optionalAuth(request, { throwOnRefresh: true });
  const context = auth ? await getContextFromSession(request) : undefined;

  const locale = await linguiServer.getLocale(request);
  const { toast, headers } = await getToast(request);

  headers.append("Set-Cookie", await localeCookie.serialize(locale));

  const utmSource = encodeURIComponent(
    url.searchParams.get("utm_source") ?? "",
  );
  if (utmSource !== "") {
    headers.append("Set-Cookie", `utm_source=${utmSource}; Path=/;`);
  }

  if (
    !url.pathname.startsWith("/auth/") &&
    import.meta.env.VITE_ENVIRONMENT !== "test"
  ) {
    // Handle callback after registration flow
    let failureRedirect = $path("/auth/login");
    if (url.searchParams.has("registrationflow")) {
      failureRedirect += "?prompt=1";
    }

    if (!auth || !auth.user) {
      if (auth?.accessToken) {
        await (await getOAuth2Strategy()).revokeToken(auth.accessToken);
      }

      return redirect(failureRedirect);
    }
  }

  const env = environment();

  return json(
    {
      locale,
      auth: auth ? { user: auth.user } : undefined,
      context,
      toast,
      bytescale: {
        accountId: env.BYTESCALE_ACCOUNT_ID,
        canUpload: auth?.user
          ? hasPermissions(
              getPermissions({
                user: auth.user,
                allPermissions: true,
              }),
              ["Media.Upload"],
            )
          : false,
        folderPath: {
          documents: `${env.BYTESCALE_FOLDER_ROOT}/documents/uploads`,
          media: `${env.BYTESCALE_FOLDER_ROOT}/media/uploads`,
          public: `${env.BYTESCALE_FOLDER_ROOT}/public/uploads`,
        },
        publicKey: env.BYTESCALE_PUBLIC_KEY,
      },
      gaTrackingIds: env.GA_TRACKING_IDS?.split(","),
      googleTagManager: {
        id: env.GTM_ID,
        clientId: env.GTM_CLIENT_ID,
      },
      metaPixel: {
        id: env.META_PIXEL_ID,
      },
      requestInfo: {
        hints: getHints(request),
        origin: getDomainUrl(request),
        path: new URL(request.url).pathname,
        tables: getTablesStates(request),
        listings: getListingDisplayStates(request),
        userPrefs: {
          theme: await getTheme(request, auth?.accessToken),
        },
      },
      environment: {
        APP_LINKS_URL: env.APP_LINKS_URL,
        AUTH_URL: env.AUTH_URL,
        CALENDLY_MEETING_URL: env.CALENDLY_MEETING_URL,
        CALENDLY_DEMO_MEETING_URL: env.CALENDLY_DEMO_MEETING_URL,
        HELPDESK_BASE_URL: env.HELPDESK_BASE_URL,
        USER_ID: auth?.uid,
      },
    },
    { headers },
  );
}

export type RootLoaderType = typeof loader;

export function Layout({ children }: { children: React.ReactNode }) {
  const data = useRouteLoaderData<typeof loader>("root");
  const locale = useLocale();
  const requestToast = useRequestToast();
  const theme = useTheme();
  const env = useEnv();
  const { user } = useOptionalAuth() ?? {};

  const bytescale = data?.bytescale;

  if (locale && i18n.locale !== locale) {
    loadCatalog(locale);
  }

  if (user) {
    Sentry.setUser({
      id: user.uid,
      email: user.email,
    });
  } else {
    Sentry.setUser(null);
  }

  return (
    <html lang={locale ?? "en"} translate="no" className={`${theme} h-full`}>
      <head>
        <ClientHintCheck />
        <meta charSet="utf-8" />
        <meta
          name="viewport"
          content="width=device-width, initial-scale=1, maximum-scale=1, user-scalable=0"
        />
        <Meta />
        <Links />
      </head>
      <body className="bg-surface-canvas text-primary font-sans antialiased min-h-full">
        <I18nProvider i18n={i18n}>
          <ClientOnly>
            <GoogleScripts />
            <MetaScripts />
          </ClientOnly>
          <ToastContextProvider serverToast={requestToast}>
            <BytescaleContextProvider accountId={bytescale?.accountId}>
              <RouteDialogContextProvider>
                {children}
              </RouteDialogContextProvider>
            </BytescaleContextProvider>
          </ToastContextProvider>
        </I18nProvider>
        <ProgressBar />
        <script
          // biome-ignore lint/security/noDangerouslySetInnerHtml: required to pass JS code
          dangerouslySetInnerHTML={{
            __html: `window.ENV = ${JSON.stringify(env)}`,
          }}
        />
        <ScrollRestoration />
        <Scripts />
      </body>
    </html>
  );
}

export function App() {
  return <Outlet />;
}

export const ErrorBoundary = () => {
  return (
    <div className="relative flex flex-col items-center text-primary font-sans antialiased min-h-screen">
      <div className="z-10 max-w-screen-xl w-full p-4">
        <Header canLogout className="bg-surface-canvas rounded-xl h-16" />
      </div>
      <main className="flex flex-1 items-center justify-center w-full -mt-4">
        <GeneralErrorBoundary />
      </main>
    </div>
  );
};

export default Sentry.wrapUseRoutesV7(App);
